/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ChipProps } from '@heroui/react';
import type { JobStatus } from './type';

/**
 * ENGAGEMENT_STATUS vocabulary — Phase 1 shared contract.
 *
 * ⚠️ The stored string VALUES are PascalCase (NOT uppercase). Every StatusChip
 * mapping, timeline, filter and comparison must key off these exact strings.
 * Only BOOKING_TYPE comparisons are uppercase ('SLOT' | 'QUOTE' | 'PACKAGE').
 *
 * `Expired` is reserved (no Phase 2 actor sets it yet) — kept in the enum only.
 *
 * `Awaiting` (provider marked the work done, awaiting the customer's
 * confirm-completion sign-off) is a real BE status as of the Awaiting
 * engagement-status rollout — previously this was a client-derived display-
 * only concept (`status === Completed && !completionConfirmedAt`); see
 * git history on isJobAwaitingCustomer/getJobDisplayStatus below for that
 * now-removed heuristic.
 */
export const ENGAGEMENT_STATUS: Record<Uppercase<JobStatus>, JobStatus> = {
  REQUESTED: 'Requested',
  QUOTED: 'Quoted',
  CHANGESREQUESTED: 'ChangesRequested',
  ACCEPTED: 'Accepted',
  SCHEDULED: 'Scheduled',
  // NOTE: `Uppercase<'InProgress'>` is `'INPROGRESS'` (no underscore) — the
  // property key MUST match so the Record type resolves.
  INPROGRESS: 'InProgress',
  AWAITING: 'Awaiting',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
} as const;

/**
 * Fixed timeline sequence for the Quote flow (annotations Quote · 4).
 * Customer-facing progress strip: Requested → Quoted → Accepted → Scheduled →
 * InProgress → Awaiting → Completed.
 */
export const JOB_TIMELINE_SEQUENCE: JobStatus[] = [
  ENGAGEMENT_STATUS.REQUESTED,
  ENGAGEMENT_STATUS.QUOTED,
  ENGAGEMENT_STATUS.ACCEPTED,
  ENGAGEMENT_STATUS.SCHEDULED,
  ENGAGEMENT_STATUS.INPROGRESS,
  ENGAGEMENT_STATUS.AWAITING,
  ENGAGEMENT_STATUS.COMPLETED,
];

/**
 * StatusChip visual mapping (PascalCase → HeroUI chip color), per annotations:
 * - Requested → warning
 * - Quoted → incomplete(blue) → primary
 * - Accepted/Scheduled/Completed → completed(green) → success
 * - InProgress → inprogress(slate) → default
 * - Cancelled/Declined → cancelled(red) → danger
 * - Expired → default(gray)
 * - Awaiting → shown in the same success tone as Completed, with its own
 *   "Awaiting" label, because it represents a provider-completed job
 *   awaiting the customer's sign-off.
 */
export const getEngagementStatusChipColor = (
  status?: string | null
): ChipProps['color'] => {
  switch (status) {
    case ENGAGEMENT_STATUS.REQUESTED:
    case ENGAGEMENT_STATUS.CHANGESREQUESTED:
      return 'warning';
    case ENGAGEMENT_STATUS.QUOTED:
      return 'primary';
    case ENGAGEMENT_STATUS.ACCEPTED:
    case ENGAGEMENT_STATUS.SCHEDULED:
    case ENGAGEMENT_STATUS.COMPLETED:
    case ENGAGEMENT_STATUS.AWAITING:
      return 'success';
    case ENGAGEMENT_STATUS.INPROGRESS:
      return 'default';
    case ENGAGEMENT_STATUS.CANCELLED:
    case ENGAGEMENT_STATUS.DECLINED:
      return 'danger';
    case ENGAGEMENT_STATUS.EXPIRED:
      return 'default';
    default:
      return 'default';
  }
};

/** Humanized label (replaces underscores/camel-case with spaced words). */
export const getEngagementStatusLabel = (status?: string | null): string => {
  if (!status) return '—';
  // InProgress → "In Progress"
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export type JobDisplayStatus = JobStatus;

/** True when a provider-completed job is awaiting the customer's sign-off. */
export const isJobAwaitingCustomer = (job: { status: JobStatus }): boolean =>
  job.status === ENGAGEMENT_STATUS.AWAITING;

/**
 * Customer-facing display status used by chips/status strips. Awaiting is
 * now a real wire status, so this is a direct passthrough — kept as a named
 * function so call sites don't need to change if that ever stops being true.
 */
export const getJobDisplayStatus = (job: {
  status: JobStatus;
}): JobDisplayStatus => job.status;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * A quote is treated as "expiring soon" once less than 48 hours remain.
 * Below this threshold the UI switches to an urgent treatment (warning tone,
 * hours countdown) instead of the plain "in N days" banner.
 */
export const QUOTE_URGENT_WINDOW_MS = 48 * HOUR_MS;

export interface QuoteExpiryInfo {
  /** Expiry timestamp in epoch ms (parsed from `quoteExpiresAt`). */
  expiresAtMs: number;
  /** Whole ms remaining until expiry (<= 0 when already expired). */
  msRemaining: number;
  /** True when `quoteExpiresAt` is in the past. */
  isExpired: boolean;
  /** True when not expired and within the 48h urgent window. */
  isUrgent: boolean;
  /** Whole days remaining (floor, min 1) — for the "in N days" copy. */
  days: number;
  /** Whole hours remaining (ceil, min 1) — for the "in N hours" copy. */
  hours: number;
}

/**
 * Derive countdown info from the BE-provided `quoteExpiresAt`. Returns null
 * when the field is absent or unparseable (legacy quotes), so callers fall
 * back to the pre-expiry UI instead of recomputing the window client-side.
 * `now` is injectable for tests.
 */
export const getQuoteExpiryInfo = (
  quoteExpiresAt?: string | null,
  now: number = Date.now()
): QuoteExpiryInfo | null => {
  if (!quoteExpiresAt) return null;
  const expiresAtMs = new Date(quoteExpiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) return null;
  const msRemaining = expiresAtMs - now;
  const isExpired = msRemaining <= 0;
  return {
    expiresAtMs,
    msRemaining,
    isExpired,
    isUrgent: !isExpired && msRemaining <= QUOTE_URGENT_WINDOW_MS,
    days: Math.max(1, Math.floor(msRemaining / DAY_MS)),
    hours: Math.max(1, Math.ceil(msRemaining / HOUR_MS)),
  };
};
